import { useRouter } from 'next/navigation';

import { Button } from '@/shared/ui/form/button/button';
import { BodyTextBoldSecondary } from '@/shared/ui/typography';
import { SkeletonLoader } from '@/shared/ui/skeleton/skeleton';
import { useI18n } from '@locales/client';
import { brandIsPh } from '@/shared/config/brand-by-target.mjs';
import { usePhSettings } from '@/features/auth-management/model/hooks/use-ph-settings';
import {
  useIsUserLoading,
  useIsAuth,
} from '@/shared/providers/user-provider/use-user';
import { ButtonWrapper } from './styles';

type AuthActionButtonsProps = {
  fillSingleButton?: boolean;
};

export function AuthActionButtons({
  fillSingleButton = false,
}: AuthActionButtonsProps) {
  const router = useRouter();
  const t = useI18n();
  const { registerFlow, isLoading } = usePhSettings();

  if (brandIsPh) {
    if (isLoading) {
      return null;
    }

    if (registerFlow !== 'full') {
      return (
        <Button
          fill={fillSingleButton}
          variant="orangeGradient"
          data-analytics-id="auth_sign_up_button"
          onClick={() => {
            router.push('/?modal=registration');
          }}
        >
          <BodyTextBoldSecondary>{t('auth.ph.submit')}</BodyTextBoldSecondary>
        </Button>
      );
    }
  }

  return (
    <>
      <Button
        variant="orangeGradient"
        data-analytics-id="auth_sign_up_button"
        onClick={() => {
          router.push('/?modal=registration');
        }}
      >
        <BodyTextBoldSecondary>{t('auth.sign_up')}</BodyTextBoldSecondary>
      </Button>
      <Button
        variant="gray"
        data-analytics-id="auth_sign_in_button"
        onClick={() => {
          router.push('/?modal=auth');
        }}
      >
        <BodyTextBoldSecondary style={{ padding: '0 8px' }}>
          {t('auth.sign_in')}
        </BodyTextBoldSecondary>
      </Button>
    </>
  );
}

export const AuthButtonsPlaceholder = () => (
  <ButtonWrapper data-auth>
    <SkeletonLoader height={40} width="100%" />
  </ButtonWrapper>
);

export default function AuthButtons() {
  const isUserLoading = useIsUserLoading();
  const isAuth = useIsAuth();

  if (isUserLoading) {
    return <AuthButtonsPlaceholder />;
  }

  if (isAuth) {
    return null;
  }

  return (
    <ButtonWrapper>
      <AuthActionButtons fillSingleButton />
    </ButtonWrapper>
  );
}
